

function trackTextBlocksAcrossFrames(frames, trackingThreshold=50, disappearanceThreshold=5, sizeThreshold = .5, confidenceThreshold = .80, maxValues) {
    let processedFrames = processOcrResults(frames);
    // console.log({processedFrames})
    let blockIdCounter = 0;
    let allTrackedBlocks = []; // To hold tracked blocks for all frames
    let previousFrameBlocks = []; // Initialize for the first frame

    const areas = processedFrames.flatMap(frame => frame.map(block => block.area));
    const maxArea = Math.max(...areas);
    const minArea = Math.min(...areas);

    // Use square root scale for threshold
    const sqrtMax = Math.sqrt(maxArea);
    const sqrtMin = Math.sqrt(minArea);
    const sqrtRange = sqrtMax - sqrtMin;
    const scaledThreshold = sqrtMin + sqrtRange * sizeThreshold;

    // Convert back to area scale
    const minSizeAllowed = scaledThreshold * scaledThreshold;

    processedFrames = processedFrames.map(frame =>
        frame.filter(block => block.area >= minSizeAllowed)
    );

    processedFrames = processedFrames.map(frame =>
    frame.map(block => ({
        ...block,
        confidence: block.confidence < 1 ? block.confidence * 100 : block.confidence
    }))
);

    // console.log(processedFrames)
    // Check only the first frame for confidence property
    const hasConfidence = processedFrames?.length > 0 && processedFrames[0][0]?.confidence > 0;

    if (hasConfidence) {
        processedFrames = processedFrames.map(frame =>
            frame.filter(block => block.confidence >= confidenceThreshold)
        );
    } else {console.log("no confidence")}
    // else: do nothing, keep all blocks in all frames
    processedFrames.forEach((currentFrame, frameIndex) => {
        let frameTrackedBlocks = []; // For the current frame

        if (frameIndex === 0) {
            // First frame: initialize tracking
            currentFrame.forEach(block => {
                const newId = ++blockIdCounter;
                frameTrackedBlocks.push({ ...block, id: newId });
                previousFrameBlocks.push({ ...block, id: newId, lastSeen: 0 });
            });
        } else {
            // Subsequent frames: match and track
            let activePreviousBlocks = previousFrameBlocks.filter(block => block.lastSeen <= disappearanceThreshold);
            // console.log({previousFrameBlocks, activePreviousBlocks})
            const matches = calculateAndMatchBlocks(currentFrame, activePreviousBlocks, trackingThreshold, maxValues);

            // Reset for the next iteration
            previousFrameBlocks = []; 
            // console.log({frameIndex, matches})
            currentFrame.forEach((block, index) => {
                const match = matches.find(m => m.currentIndex === index);
                if (match) {
                    const matchedBlock = activePreviousBlocks[match.previousIndex];
                    frameTrackedBlocks.push({ ...block, id: matchedBlock.id });
                    previousFrameBlocks.push({ ...block, id: matchedBlock.id, lastSeen: 0 });
                } else {
                    // New or unmatched block
                    const newId = ++blockIdCounter;
                    frameTrackedBlocks.push({ ...block, id: newId });
                    previousFrameBlocks.push({ ...block, id: newId, lastSeen: 0 });
                }
            });

            // detects transitive property driven duplicate ids due to disappearance and just assigned new
            // crude and ruins some frame blocks but at least no duplicates
            let idCounter = {};
              frameTrackedBlocks.forEach(block => {
                  if (idCounter[block.id]) {
                      block.id = ++blockIdCounter;
                  } else {
                      idCounter[block.id] = true;
                  }
              });
            // if (frameIndex===13) console.log({frameTrackedBlocks, previousFrameBlocks})
            // Update lastSeen for unmatched previous blocks
            activePreviousBlocks.forEach(block => {
                if (!matches.some(m => m.previousIndex === block.originalIndex)) {
                    block.lastSeen += 1;
                    previousFrameBlocks.push(block); // Continue tracking
                }
            });
        }
        // if (frameIndex===6) console.log({frameIndex, frameTrackedBlocks})

        allTrackedBlocks.push(frameTrackedBlocks);
    });

    // After processing all frames
    // console.log({allTrackedBlocks})
    allTrackedBlocks.forEach((frame, index) => {
      let allBlockIds = new Set();
        frame.forEach(block => {
            if (allBlockIds.has(block.id)) {
                console.log(`Duplicate block ID found: ${block.id} for frame: ${index}`);
            } else {
                allBlockIds.add(block.id);
            }
        });
    });
    
    return allTrackedBlocks; // Contains tracked blocks for each frame
}


function calculateAndMatchBlocks(currentFrameBlocks, activePreviousBlocks, trackingThreshold, maxValues) {
    // Gather all match candidates with similarity scores above the threshold
    let candidates = [];
    // console.log({currentFrameBlocks})
    currentFrameBlocks.forEach((currentBlock, currentIndex) => {
        activePreviousBlocks.forEach((previousBlock, previousIndex) => {
            let similarityScore = calculateBlockSimilarity(previousBlock, currentBlock, maxValues);
            if (similarityScore >= trackingThreshold/100) {
                candidates.push({
                    currentIndex,
                    previousIndex,
                    similarityScore
                });
            }
        });
    });

    // Sort candidates by similarity score, highest first
    candidates.sort((a, b) => b.similarityScore - a.similarityScore);
    // console.log(candidates)
    let matches = [];
    let matchedCurrentIndices = new Set();
    let matchedPreviousIndices = new Set();

    // Iterate over sorted candidates and select matches, ensuring unique matches
    candidates.forEach(candidate => {
        if (!matchedCurrentIndices.has(candidate.currentIndex) && !matchedPreviousIndices.has(candidate.previousIndex)) {
            matches.push({
                currentIndex: candidate.currentIndex,
                previousIndex: candidate.previousIndex
            });
            matchedCurrentIndices.add(candidate.currentIndex);
            matchedPreviousIndices.add(candidate.previousIndex);
        }
    });
    return matches;
}

function calculateDistance(boxA, boxB) {
  if (!Array.isArray(boxA.vertices) || boxA.vertices.length < 3 ||
      !Array.isArray(boxB.vertices) || boxB.vertices.length < 3) {
    return { x: 0, y: 0 }; // Or handle as needed
  }
  const centerA = {
    x: (boxA.vertices[0].x + boxA.vertices[2].x) / 2,
    y: (boxA.vertices[0].y + boxA.vertices[2].y) / 2,
  };
  const centerB = {
    x: (boxB.vertices[0].x + boxB.vertices[2].x) / 2,
    y: (boxB.vertices[0].y + boxB.vertices[2].y) / 2,
  };
  return {
    x: Math.abs(centerA.x - centerB.x),
    y: Math.abs(centerA.y - centerB.y)
  };
}

function calculateBlockSimilarity(blockA, blockB, maxValues) {
  const maxDistance = maxValues ? maxValues.maxDistance : 1000;
  const maxSizeDifference = maxValues ? maxValues.maxSizeDifference : 5000;
  const maxTextLengthDifference = maxValues ? maxValues.maxTextLength : 40;
  const distance = calculateDistance(blockA, blockB);
  const normalizedDistanceX = 1 - Math.min(distance.x / maxDistance, 1);
  const normalizedDistanceY = 1 - Math.min(distance.y / maxDistance, 1);
  const normalizedSizeDifference = 1 - Math.min(calculateSizeDifference(blockA, blockB) / maxSizeDifference, 1);
  const normalizedTextLengthDifference = 1 - Math.min(Math.abs(blockA.text.length - blockB.text.length) / maxTextLengthDifference, 1);

  const distanceWeightX = 0.7; // Adjust this weight as needed
  const distanceWeightY = 0.3; // Adjust this weight as needed
  const sizeDifferenceWeight = 0;
  const textLengthDifferenceWeight = 0;

  const similarityScore = normalizedDistanceX * distanceWeightX +
                          normalizedDistanceY * distanceWeightY +
                          normalizedSizeDifference * sizeDifferenceWeight +
                          normalizedTextLengthDifference * textLengthDifferenceWeight;

  return similarityScore;
}

function calculateSizeDifference(blockA, blockB) {
  // Example calculation: difference in the area of the bounding boxes
    if (!Array.isArray(blockA.vertices) || blockA.vertices.length < 3 ||
      !Array.isArray(blockB.vertices) || blockB.vertices.length < 3) {
    return { x: 0, y: 0 }; // Or handle as needed
  }
  const areaA = (blockA.vertices[2].x - blockA.vertices[0].x) * (blockA.vertices[2].y - blockA.vertices[0].y);
  const areaB = (blockB.vertices[2].x - blockB.vertices[0].x) * (blockB.vertices[2].y - blockB.vertices[0].y);
  return Math.abs(areaA - areaB);
}

// Helper function to compute max, min, average, mode, and median
function computeStatistics(scores) {
  if (scores.length === 0) return {};

  // Sort scores for median calculation
  scores.sort((a, b) => a - b);
  const total = scores.reduce((acc, score) => acc + score, 0);
  const average = total / scores.length;
  const max = Math.max(...scores);
  const min = Math.min(...scores);
  const median = scores.length % 2 === 0
    ? (scores[scores.length / 2 - 1] + scores[scores.length / 2]) / 2
    : scores[Math.floor(scores.length / 2)];

  // Calculate mode
  const frequencyMap = {};
  scores.forEach(score => {
    if (!frequencyMap[score]) frequencyMap[score] = 0;
    frequencyMap[score]++;
  });
  const maxFrequency = Math.max(...Object.values(frequencyMap));
  const mode = Object.keys(frequencyMap).filter(key => frequencyMap[key] === maxFrequency).map(parseFloat);

  return { max, min, average, median, mode };
}

function processOcrResults(ocrResults) {
  return ocrResults.map((result) => {
    return Array.isArray(result.detections) ? result.detections.map((detection) => {
      // Start with a base object for mandatory properties
      const processedDetection = {
        text: detection.text || "", // Default to empty string if text is undefined
        vertices: detection.vertices || [], // Default to empty array if vertices are undefined
        area: detection.area || 0, // Default to 0 if area is undefined
        confidence: detection?.confidence || 0, // Default to 0 if confidence is undefined
      };

      // Add originalText only if it exists
      if (detection.originalText) {
        processedDetection.originalText = detection.originalText;
      }

      return processedDetection;
    }) : []; // Return an empty array if detections is not an array or undefined
  });
}




function identifyChangingTextBlocks(firestoreFormattedResults) {
  const changingBlocks = new Set(); // Set to hold IDs of changing text blocks

  for (let i = 0; i < firestoreFormattedResults.length - 1; i++) {
    const currentFrame = firestoreFormattedResults[i];
    const nextFrame = firestoreFormattedResults[i + 1];

    currentFrame.blocks.forEach((currentBlock) => {
      const matchingBlockInNextFrame = nextFrame.blocks.find((nextBlock) => nextBlock.id === currentBlock.id);

      if (matchingBlockInNextFrame && currentBlock.text !== matchingBlockInNextFrame.text) {
        changingBlocks.add(currentBlock.id);
      }
    });
  }

  return Array.from(changingBlocks); // Convert the set to an array
}

function calculateDiagonalDistance(vertices) {
    const width = vertices[1].x - vertices[0].x;
    const height = vertices[2].y - vertices[1].y;
    return Math.sqrt(width * width + height * height);
}

export {trackTextBlocksAcrossFrames}